import React, { useState, useEffect, useRef } from 'react';
import useSidebarResizing from '../hooks/useSidebarResizing';
import { useDispatch, useSelector } from 'react-redux';
import DjChangeBanner from './DjChangeBanner';
import { wrappablePath } from '../utils/formatPath';
import { formatDjImportSummary, formatDjImportStopped } from '../utils/djImportSummary';
import SendToAppModal from './SendToAppModal';
import SyncFromAppModal from './SyncFromAppModal';
import { getImportExclusions, localPlaylistSelectionKey } from '../utils/djImportSelection';
import AutoSyncToggle from './AutoSyncToggle';
import DjLibraryMeter from './DjLibraryMeter';
import DjLocationControls from './DjLocationControls';
import { persistDjSidebarWidth, DJ_SIDEBAR_MIN_WIDTH, DJ_SIDEBAR_MAX_WIDTH } from '../redux/utils/djSidebarWidth';
import useAutoSyncNudge from '../hooks/useAutoSyncNudge';
import seratoLogo from '../assets/serato-logo-10px.png';
import { track } from '../utils/analytics';
import { clearSyncRequest } from '../redux/slices/djLibraryChangesSlice';
import { Button, Spinner, ListGroup, Badge } from 'react-bootstrap';
import ErrorBanner from './ErrorBanner';
import {
    loadPersistedSeratoState,
    toggleSeratoSidebar,
    setSeratoSidebarWidth,
    setSeratoSyncStatus,
    setSeratoPath,
    setSeratoExists,
    setSeratoPlaylists,
    setSeratoLastSyncTime,
    setSeratoImporting,
    setSeratoExporting,
    setSeratoError
} from '../redux/slices/seratoSlice';
import { triggerPlaylistRefresh } from '../redux/slices/playlistSlice';
import { useToast } from '../contexts/ToastContext';
import './SeratoSidebar.scss';
import { errMsg } from '../redux/utils/errMsg';

const SeratoSidebar = () => {
    const dispatch = useDispatch();
    const toast = useToast();
    const {
        sidebarOpen,
        sidebarWidth,
        syncStatus,
        seratoPath,
        seratoExists,
        seratoPlaylists,
        lastSyncTime,
        importing,
        exporting,
        error
    } = useSelector(state => state.serato);

    const authUserId = useSelector((state) => state.auth?.user?.id) || null;
    const maybeNudgeAutoSync = useAutoSyncNudge('serato', { label: 'Serato', noun: 'crate', logo: seratoLogo });
    const [isResizing, setIsResizing] = useState(false);
    useSidebarResizing(isResizing);
    const sidebarRef = useRef(null);
    const widthRef = useRef(sidebarWidth);
    widthRef.current = sidebarWidth;
    const [hasCheckedSerato, setHasCheckedSerato] = useState(false);
    const [seratoType, setSeratoType] = useState(null);
    const [seratoVersion, setSeratoVersion] = useState(null);

    // Load persisted state on mount
    useEffect(() => {
        const loadAsync = async () => {
            await new Promise(resolve => setTimeout(resolve, 0));

            try {
                // "Last synced" is not read here: it belongs to the account and is
                // hydrated from the main process by DjSyncStateHydrator.
                const seratoPath = localStorage.getItem('serato.path') || null;
                const seratoPlaylists = JSON.parse(localStorage.getItem('serato.playlists')) || [];

                dispatch(loadPersistedSeratoState({
                    seratoPath,
                    seratoPlaylists
                }));
            } catch (e) {
                console.error('Failed to load persisted serato state:', e);
                dispatch(loadPersistedSeratoState({}));
            }
        };
        loadAsync();
    }, [dispatch]);

    // Save state on app close
    useEffect(() => {
        const saveOnClose = () => {
            try {
                localStorage.setItem('serato.path', seratoPath);
                localStorage.setItem('serato.playlists', JSON.stringify(seratoPlaylists));
            } catch (e) {
                // Silently fail
            }
        };

        window.addEventListener('beforeunload', saveOnClose);
        return () => window.removeEventListener('beforeunload', saveOnClose);
    }, [seratoPath, seratoPlaylists]);

    // Load imported playlists from database
    const loadImportedPlaylists = async (forceRefresh = false) => {
        if (!forceRefresh && seratoPlaylists.length > 0) {
            return seratoPlaylists;
        }

        try {
            // Pass `false` — display only needs name + trackCount. Asking
            // for tracks here would IPC every track in every playlist.
            const allPlaylists = await window.electron.db.playlists.getAll(false);
            const seratoImported = allPlaylists.filter(p =>
                (p.apps && p.apps.includes('serato')) || (p.description && p.description.includes('Imported from Serato'))
            );
            dispatch(setSeratoPlaylists(seratoImported));
            return seratoImported;
        } catch (err) {
            console.error('[SeratoSidebar] Error loading imported playlists:', err);
            return [];
        }
    };

    // On sidebar open
    useEffect(() => {
        if (!sidebarOpen) return;

        if (seratoPlaylists.length > 0) {
            if (syncStatus !== 'partial') dispatch(setSeratoSyncStatus('synced'));

            if (!hasCheckedSerato) {
                checkSerato();
                setHasCheckedSerato(true);
            }
            return;
        }

        const initialize = async () => {
            if (!hasCheckedSerato) {
                dispatch(setSeratoSyncStatus('checking'));
            }

            // Run both checks in parallel for faster load
            const [playlists] = await Promise.all([
                loadImportedPlaylists(),
                !hasCheckedSerato ? checkSerato() : Promise.resolve()
            ]);

            if (!hasCheckedSerato) {
                setHasCheckedSerato(true);
            }

            if (syncStatus !== 'partial') dispatch(setSeratoSyncStatus(playlists.length > 0 ? 'synced' : 'not_synced'));
        };

        initialize();
    }, [sidebarOpen]);

    // Handle resize
    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e) => {
            // The sidebar sits right of the icon rail (position:fixed with a
            // left offset) — width is cursor minus its left edge, not raw clientX.
            const left = sidebarRef.current?.getBoundingClientRect().left ?? 0;
            const newWidth = e.clientX - left;
            if (newWidth >= DJ_SIDEBAR_MIN_WIDTH && newWidth <= DJ_SIDEBAR_MAX_WIDTH) {
                dispatch(setSeratoSidebarWidth(newWidth));
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            persistDjSidebarWidth(widthRef.current);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, dispatch]);

    const checkSerato = async () => {
        try {
            const result = await window.electron.serato.checkExists();
            dispatch(setSeratoPath(result.path));
            dispatch(setSeratoExists(result.exists));
            setSeratoType(result.type);
            setSeratoVersion(result.version);
            return !!result.exists;
        } catch (err) {
            console.error('Error checking Serato:', err);
            dispatch(setSeratoError(err.message));
            return false;
        }
    };

    // Re-scan must SAY what it found — a re-check that ends in the same
    // "not found" card changes nothing on screen and reads as a dead button.
    const rescanSerato = async () => {
        const found = await checkSerato();
        if (found) toast.success('Serato library found');
        else toast.info('Still no Serato library in the usual folders — if yours lives somewhere else, use Locate\u2026');
    };

    // opts.playlistKeys — explicit picker selection (SyncFromAppModal).
    // Without it, the persisted exclusions apply, so one-click re-syncs
    // never re-import crates the user unchecked.
    const handleImport = async (opts = {}) => {
        const playlistKeys = Array.isArray(opts.playlistKeys) ? opts.playlistKeys : null;
        const excludeKeys = playlistKeys ? [] : getImportExclusions('serato', authUserId);
        const wasFirstSync = !lastSyncTime; // captured before setSeratoLastSyncTime below
        dispatch(setSeratoImporting(true));
        const importStartedAt = Date.now();
        try {
            const result = await window.electron.serato.importPlaylists({
                playlistKeys: playlistKeys || undefined,
                excludeKeys: excludeKeys.length ? excludeKeys : undefined,
            });

            // Stopped from the picker: playlists imported before the stop are
            // COMPLETE (each commits together with its tracks); the rest —
            // and the sync-complete mark — wait for the next run.
            if (result?.canceled) {
                dispatch(triggerPlaylistRefresh());
                const kept = await loadImportedPlaylists(true);
                // A stop that changed nothing leaves the previous state true:
                // a synced library stays "synced", not "incomplete". Only a
                // run that actually landed content degrades the badge.
                const stopChanged = (result.imported ?? 0) + (result.updated ?? 0) + (result.tracksCreated ?? 0) + (result.tracksUpdated ?? 0) > 0;
                if (stopChanged || syncStatus !== 'synced') dispatch(setSeratoSyncStatus(kept.length ? 'partial' : 'not_synced'));
                track('dj_import_canceled', {
                    app: 'serato',
                    playlists_imported: result.imported ?? 0,
                    tracks_created: result.tracksCreated ?? 0,
                    tracks_updated: result.tracksUpdated ?? 0,
                    duration_ms: Date.now() - importStartedAt,
                });
                toast.info(formatDjImportStopped(result, { noun: 'crate' }));
                return;
            }

            dispatch(setSeratoLastSyncTime(new Date().toISOString()));
            dispatch(setSeratoSyncStatus('synced'));
            dispatch(triggerPlaylistRefresh());

            await loadImportedPlaylists(true);

            track('dj_playlists_imported', {
                app: 'serato',
                mode: playlistKeys ? 'selected' : 'all',
                excluded_count: excludeKeys.length,
                imported: result.imported ?? 0,
                updated: result.updated ?? 0,
                tracks_created: result.tracksCreated ?? 0,
                tracks_updated: result.tracksUpdated ?? 0,
                from: opts.from || 'sidebar',
                duration_ms: Date.now() - importStartedAt,
            });
            if (!maybeNudgeAutoSync(result, opts.from, { firstSync: wasFirstSync })) {
                toast.success(formatDjImportSummary(result, { app: 'Serato', noun: 'crate' }));
            }
        } catch (err) {
            console.error('Error importing from Serato:', err);
            toast.error(`Import failed: ${errMsg(err)}`);
            // Playlists commit whole, so a failure can still leave complete
            // imports behind — keep them visible; the badge says incomplete.
            const kept = await loadImportedPlaylists(true);
            dispatch(setSeratoSyncStatus(kept.length ? 'partial' : 'error'));
            dispatch(setSeratoError(err.message));
        } finally {
            dispatch(setSeratoImporting(false));
        }
    };

    // "Sync now" from the toast card / OS notification opens this sidebar and
    // asks it to import once it's mounted.
    const [showSend, setShowSend] = useState(false);
    const [showImportPicker, setShowImportPicker] = useState(false);

    // Local playlists are owner-scoped (migration 012): on account switch or
    // session restore, drop the previous account's cached list and refetch.
    const prevUserRef = useRef(authUserId);
    useEffect(() => {
        if (prevUserRef.current === authUserId) return;
        prevUserRef.current = authUserId;
        dispatch(setSeratoPlaylists([]));
        if (sidebarOpen) loadImportedPlaylists(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authUserId]);
    const syncRequested = useSelector((state) => !!state.djLibraryChanges?.syncRequested?.serato);
    const pendingDiff = useSelector((state) => state.djLibraryChanges?.pending?.serato?.diff);
    useEffect(() => {
        if (!syncRequested) return;
        dispatch(clearSyncRequest('serato'));
        // A change that includes never-imported playlists is a choice, not a
        // one-click action — open the picker (new rows start unchecked) so
        // hundreds of unimported playlists can't flood in behind one click.
        // Changes to already-synced playlists alone stay one-click.
        if (pendingDiff?.newPlaylists?.length) setShowImportPicker(true);
        else handleImport({ from: 'watcher_card' });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [syncRequested]);

    const handleExport = async () => {
        // Get all playlists from database
        let playlistsToExport = [];
        try {
            const allPlaylists = await window.electron.db.playlists.getAll(true);
            playlistsToExport = allPlaylists;
        } catch (err) {
            console.error('Error fetching playlists:', err);
            toast.error('Failed to fetch playlists');
            return;
        }

        if (playlistsToExport.length === 0) {
            toast.warning('No playlists to export');
            return;
        }

        const totalTracks = playlistsToExport.reduce((sum, p) => sum + (p.tracks?.length || 0), 0);

        // Show save dialog - export to folder (creates multiple M3U files)
        const result = await window.electron.dialog.showOpenDialog({
            title: 'Select folder to export playlists for Serato',
            properties: ['openDirectory', 'createDirectory']
        });

        if (result.canceled || !result.filePaths.length) return;

        const outputDir = result.filePaths[0];

        const confirmed = await toast.confirm(
            `This will export ${playlistsToExport.length} playlist${playlistsToExport.length !== 1 ? 's' : ''} with ${totalTracks} track${totalTracks !== 1 ? 's' : ''} as M3U files to:\n\n${outputDir}\n\nTo import in Serato:\nFile > Import Crate > Select the exported M3U files`,
            'Export for Serato',
            { confirmText: 'Export', confirmVariant: 'primary' }
        );

        if (!confirmed) return;

        dispatch(setSeratoExporting(true));
        try {
            await window.electron.serato.exportPlaylists(playlistsToExport, outputDir);
            toast.success(`Exported ${playlistsToExport.length} playlist${playlistsToExport.length !== 1 ? 's' : ''} to ${outputDir}`);
        } catch (err) {
            console.error('Error exporting playlists:', err);
            toast.error(`Export failed: ${errMsg(err)}`);
        } finally {
            dispatch(setSeratoExporting(false));
        }
    };

    const formatSyncTime = (isoString) => {
        if (!isoString) return 'Never';
        const date = new Date(isoString);
        // Persisted "null"/garbage (fresh profiles) must not show "Invalid Date".
        return isNaN(date.getTime()) ? 'Never' : date.toLocaleString();
    };

    if (!sidebarOpen) return null;

    // Which imported playlists the user excluded from sync — kept locally by
    // design, skipped by re-syncs. localStorage read per render is cheap; the
    // Manage-imports picker stays the source of truth.
    const excludedKeySet = new Set(getImportExclusions('serato', authUserId));
    const isExcludedFromSync = (p) => { const k = localPlaylistSelectionKey('serato', p); return !!k && excludedKeySet.has(k); };

    return (
        <div ref={sidebarRef} className={`serato-sidebar${isResizing ? ' resizing' : ''}`} style={{ width: `${sidebarWidth}px` }}>
            <SendToAppModal app="serato" show={showSend} onClose={() => setShowSend(false)} onSent={() => loadImportedPlaylists?.(true)} />
            <SyncFromAppModal app="serato" show={showImportPicker} onClose={() => setShowImportPicker(false)} userId={authUserId} onImport={(keys) => handleImport({ playlistKeys: keys, from: 'picker' })} />
            <div className="sidebar-header">
                <div className="header-title">
                    <h4>Serato Sync</h4>
                    <button
                        className="sidebar-collapse-btn"
                        onClick={() => dispatch(toggleSeratoSidebar())}
                        title="Hide Serato"
                    >
                        ‹
                    </button>
                </div>

                <div className="sync-status">
                    {syncStatus === 'checking' && (
                        <Badge bg="secondary">
                            <Spinner animation="border" size="sm" /> Checking...
                        </Badge>
                    )}
                    {syncStatus === 'synced' && <Badge bg="success">Playlists imported</Badge>}
                    {/* Stopped or failed mid-import: whole playlists are in
                        (each commits with its tracks) but the mirror isn't
                        complete yet — neither "imported" nor "no playlists"
                        is true, so say the third thing. */}
                    {syncStatus === 'partial' && <Badge bg="warning" text="dark">Sync incomplete</Badge>}
                    {/* "Not Synced" measured PLAYLISTS but sat beside a track
                        count, so a library with thousands of tracks imported
                        read as not synced at all — two true statements that
                        looked like a contradiction. Say which one this is. */}
                    {syncStatus === 'not_synced' && seratoExists && (
                        <Badge bg="secondary">No playlists imported</Badge>
                    )}
                    {syncStatus === 'not_synced' && !seratoExists && (
                        <Badge bg="secondary">Not Found</Badge>
                    )}
                    {syncStatus === 'error' && <Badge bg="danger">Error</Badge>}
                </div>
            </div>

            <div className="sidebar-content">
                {(syncStatus === 'synced' || syncStatus === 'partial') ? (
                    <div className="synced-view">
                        <div className="sync-info">
                            <DjChangeBanner app="serato" onSync={() => handleImport({ from: 'banner' })} syncing={importing} />
                            <p className="last-sync">Last synced: {formatSyncTime(lastSyncTime)}</p>
                            <p className="serato-path" title={seratoPath}>{wrappablePath(seratoPath)}</p>
                        </div>

                        <AutoSyncToggle app="serato" label="Serato" noun="crate" />

                        <DjLibraryMeter app="serato" exists={seratoExists} userId={authUserId} importing={importing} onManage={() => setShowImportPicker(true)} noun="crate" />

                        <div className="section">
                            <h5>Imported Crates ({seratoPlaylists.length})</h5>
                            {seratoPlaylists.length > 0 ? (
                                <ListGroup variant="flush" className="playlist-list">
                                    {seratoPlaylists.map((playlist) => (
                                        <ListGroup.Item key={playlist.id} className="playlist-item">
                                            <span className="playlist-name">{playlist.name}</span>
                                            {isExcludedFromSync(playlist) && <span className="dj-not-syncing-tag">not syncing</span>}
                                            <Badge bg="secondary" className="track-count">
                                                {playlist.trackCount || 0} tracks
                                            </Badge>
                                        </ListGroup.Item>
                                    ))}
                                </ListGroup>
                            ) : (
                                <p className="empty-message">No crates imported yet</p>
                            )}
                        </div>

                        <div className="action-buttons">
                            <Button
                                variant="outline-primary"
                                onClick={() => handleImport({ from: 'resync' })}
                                disabled={importing}
                                className="action-btn"
                            >
                                {importing ? (
                                    <><Spinner animation="border" size="sm" /> Re-importing...</>
                                ) : (
                                    'Re-sync from Serato'
                                )}
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowImportPicker(true)}
                                disabled={importing}
                                className="action-btn"
                            >
                                Manage imports…
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => setShowSend(true)}
                                className="action-btn"
                            >
                                Send playlists to Serato…
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="not-synced-view">
                        <div className="section detection-section">
                            <h5>Serato Library</h5>
                            {seratoExists ? (
                                <div className="detected">
                                    <div className="status-icon success">&#10003;</div>
                                    <div className="status-text">
                                        <p className="status-label">Serato library found</p>
                                        <p className="serato-path" title={seratoPath}>{wrappablePath(seratoPath)}</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="not-detected">
                                    <div className="status-icon warning">!</div>
                                    <div className="status-text">
                                        <p className="status-label">Serato not found</p>
                                        <p className="hint">Make sure Serato DJ is installed</p>
                                    </div>
                                </div>
                            )}
                            <div className="detection-actions">
                                {/* Re-scan is a retry for FAILED detection — once the
                                    library is found it's noise next to "Change
                                    location…" (two look-alike text links). */}
                                {!seratoExists && (
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={rescanSerato}
                                        className="refresh-btn"
                                        title="Search the usual locations for your Serato library again"
                                    >
                                        Re-scan
                                    </Button>
                                )}
                                <DjLocationControls app="serato" label="Serato" exists={hasCheckedSerato ? seratoExists : null} onChanged={checkSerato} />
                            </div>
                        </div>

                        <div className="section import-section">
                            <h5>Import from Serato</h5>
                            <div className="safety-warning">
                                <span className="warning-icon">&#9432;</span>
                                <span>Reads your Serato crates. Your library will NOT be modified.</span>
                            </div>

                            <DjLibraryMeter compact app="serato" exists={seratoExists} userId={authUserId} />

                            <Button
                                variant="primary"
                                onClick={() => setShowImportPicker(true)}
                                disabled={!seratoExists || importing}
                                className="action-btn import-btn"
                            >
                                {importing ? (
                                    <><Spinner animation="border" size="sm" /> Importing...</>
                                ) : (
                                    'Import from Serato'
                                )}
                            </Button>
                        </div>

                        <div className="section export-section">
                            <h5>Send to Serato</h5>
                            <p className="section-hint">Put MixGoat playlists into your Serato library — pick which ones, only local files are sent.</p>
                            <Button variant="secondary" onClick={() => setShowSend(true)} className="action-btn export-btn">
                                Send playlists to Serato…
                            </Button>
                        </div>

                    </div>
                )}
                {/* Both views: a failed re-sync must not hide the imported
                    playlists it didn't touch. */}
                {error && (
                    <ErrorBanner
                        variant="inline"
                        error={error}
                        surface="serato_sync"
                        onRetry={() => {
                            dispatch(setSeratoError(null));
                            if (syncStatus === 'error' || syncStatus === 'partial') handleImport();
                            else checkSerato();
                        }}
                    />
                )}
            </div>

            <div className="sidebar-resizer" onMouseDown={() => setIsResizing(true)} />
        </div>
    );
};

export default SeratoSidebar;
